import type { ProductWithCategories } from '@/lib/api/products';
import type { Category } from '@/types';
import { formatWeight } from '@/lib/utils';
import type { StorefrontArt } from '@/lib/api/storefront-media';

// An admin image replacement always wins over the approved web art. Otherwise
// an old food photograph/scene would silently mask the new product indefinitely.
// Art is used only together with its studio cutout; without art the app photo stays.
export function productPresentation(product: ProductWithCategories, art?: StorefrontArt | null) {
  const approved = art?.studio && art.source === product.image_url ? art : undefined;
  return {
    image: approved?.studio?.url || product.image_url || '/placeholder.png',
    scene: approved?.scene || undefined,
    studio: approved?.studio || undefined,
    contact: approved?.contact || undefined,
    studioRetouched: !!approved,
    wide: !!approved?.wide,
  };
}

export function isOrderable(product: ProductWithCategories) {
  return (
    product.status === 'available' &&
    product.is_visible !== false &&
    Number.isFinite(Number(product.price)) &&
    Number(product.price) >= 0
  );
}

export function visibleCatalog(products: ProductWithCategories[], categories: Category[]) {
  const ids = new Set(categories.filter((c) => c.is_visible !== false).map((c) => c.id));
  return products.filter(
    (p) =>
      p.is_visible !== false && p.status !== 'hidden' && p.categories.some((c) => ids.has(c.id))
  );
}

export function catalogGroups(products: ProductWithCategories[], categories: Category[]) {
  const visible = visibleCatalog(products, categories);
  return [...categories]
    .filter((c) => c.is_visible !== false)
    .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
    .map((category) => ({
      ...category,
      name: category.name.trim(),
      products: visible
        .filter((p) => p.categories.some((c) => c.id === category.id))
        .sort((a, b) => a.price - b.price || a.id - b.id),
    }))
    .filter((category) => category.products.length);
}

export function toDetailProduct(product: ProductWithCategories, art?: StorefrontArt | null) {
  return {
    ...product,
    ...productPresentation(product, art),
    price: Number(product.price),
    weight: formatWeight(product.weight),
  };
}

export function paidQuantities(
  items: { id: number; quantity: number; isBonus?: boolean; bonusType?: unknown }[]
) {
  return Object.fromEntries(
    items.filter((item) => !item.isBonus && !item.bonusType).map((item) => [item.id, item.quantity])
  );
}

export function validateAdditions(
  additions: { id: number; quantity: number }[],
  products: ProductWithCategories[],
  quantities: Record<string, number>
) {
  if (!additions.length) return false;
  const merged = new Map<number, number>();
  for (const { id, quantity } of additions) {
    const product = products.find((p) => p.id === id);
    if (!product || !isOrderable(product) || !Number.isInteger(quantity) || quantity < 1)
      return false;
    merged.set(id, (merged.get(id) || 0) + quantity);
  }
  return [...merged].every(([id, quantity]) => (quantities[id] || 0) + quantity <= 99);
}
